// src/pages/Appli/AppliAdmin/VisitorCenter/hooks/useLiveVisitors.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getLiveVisitors,
  getVisitorSessionById,
  getVisitorTrackingStats,
} from "./../../../../../services/visitorTrackingServices";

export default function useLiveVisitors({
  enabled = true,
  pollMs = 8000,
} = {}) {
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState(null);

  const [selectedVisitorId, setSelectedVisitorId] =
    useState(null);

  const [selectedVisitor, setSelectedVisitor] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const refresh = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);

      setErrorMessage("");

      const [liveResponse, statsResponse] =
        await Promise.all([
          getLiveVisitors(),
          getVisitorTrackingStats(),
        ]);

      const nextVisitors = liveResponse?.data || [];

      setVisitors(nextVisitors);
      setStats(statsResponse?.data || null);

      setSelectedVisitorId((currentId) => {
        if (currentId) return currentId;

        return nextVisitors[0]?.id || null;
      });
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          "Impossible de récupérer les visiteurs en direct.",
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const selectVisitor = useCallback(async (visitorId) => {
    if (!visitorId) {
      setSelectedVisitorId(null);
      setSelectedVisitor(null);
      return;
    }

    try {
      setSelectedVisitorId(visitorId);
      setDetailsLoading(true);

      const response =
        await getVisitorSessionById(visitorId);

      setSelectedVisitor(response?.data || null);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          "Impossible de récupérer le détail du visiteur.",
      );
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    refresh();

    const intervalId = window.setInterval(() => {
      refresh({ silent: true });
    }, pollMs);

    return () => window.clearInterval(intervalId);
  }, [enabled, pollMs, refresh]);

  useEffect(() => {
    if (!enabled || !selectedVisitorId) return;

    selectVisitor(selectedVisitorId);
  }, [enabled, selectVisitor, selectedVisitorId]);

  return {
    visitors,
    stats,

    selectedVisitorId,
    selectedVisitor,

    loading,
    detailsLoading,
    errorMessage,

    refresh,
    selectVisitor,
  };
}